/** Custom HTML5 DnD mime used when dragging a survey drop-pin onto the map. */
export const SURVEY_IMAGE_DRAG_MIME = 'application/x-xa-survey-slug';

/**
 * Fallback max distance (meters) when zoom/resolution are unavailable.
 * Prefer {@link resolveMaxDropDistanceMeters} so tolerance scales with zoom.
 */
export const MAX_DROP_TO_SURVEY_IMAGE_DISTANCE_METERS = 50;

/** Zoom at (or below) which the largest drop radius applies. */
export const DROP_DISTANCE_ZOOM_OUT = 8;
/** Zoom at (or above) which the smallest drop radius applies. */
export const DROP_DISTANCE_ZOOM_IN = 20;
/** Drop radius (meters) when zoomed out. */
export const DROP_DISTANCE_METERS_AT_ZOOM_OUT = 900;
/** Drop radius (meters) when zoomed in. */
export const DROP_DISTANCE_METERS_AT_ZOOM_IN = 12;

/**
 * Target on-screen radius for resolution-based scaling.
 * Low zoom -> large metres/pixel -> large allowed drop area.
 */
export const DROP_DISTANCE_SCREEN_RADIUS_PX = 64;

export type ResolveMaxDropDistanceOptions = {
  readonly zoom?: number | null;
  readonly resolution?: number | null;
};

function clampDropDistanceMeters(meters: number): number {
  return Math.round(
    Math.min(
      DROP_DISTANCE_METERS_AT_ZOOM_OUT,
      Math.max(DROP_DISTANCE_METERS_AT_ZOOM_IN, meters),
    ),
  );
}


export function resolutionToMetersPerPixel(resolution: number): number {
  if (!Number.isFinite(resolution) || resolution <= 0) {
    return Number.NaN;
  }
  if (resolution < 0.01) {
    return resolution * 111_320;
  }
  return resolution;
}

/**
 * Larger allowed drop area when zoomed out; tighter when zoomed in.
 * Prefers map resolution (scales continuously with zoom). Falls back to a
 * linear zoom interpolation when resolution is unavailable.
 */
export function resolveMaxDropDistanceMeters(
  zoomOrOptions?: number | null | ResolveMaxDropDistanceOptions,
  maybeResolution?: number | null,
): number {
  const options: ResolveMaxDropDistanceOptions =
    typeof zoomOrOptions === 'object' && zoomOrOptions !== null
      ? zoomOrOptions
      : { zoom: zoomOrOptions, resolution: maybeResolution };

  const { zoom, resolution } = options;

  if (typeof resolution === 'number' && Number.isFinite(resolution) && resolution > 0) {
    const metersPerPixel = resolutionToMetersPerPixel(resolution);
    if (Number.isFinite(metersPerPixel) && metersPerPixel > 0) {
      return clampDropDistanceMeters(
        metersPerPixel * DROP_DISTANCE_SCREEN_RADIUS_PX,
      );
    }
  }

  if (typeof zoom !== 'number' || !Number.isFinite(zoom)) {
    return MAX_DROP_TO_SURVEY_IMAGE_DISTANCE_METERS;
  }

  const span = DROP_DISTANCE_ZOOM_IN - DROP_DISTANCE_ZOOM_OUT;
  const t = Math.min(1, Math.max(0, (zoom - DROP_DISTANCE_ZOOM_OUT) / span));
  return Math.round(
    DROP_DISTANCE_METERS_AT_ZOOM_OUT +
      t * (DROP_DISTANCE_METERS_AT_ZOOM_IN - DROP_DISTANCE_METERS_AT_ZOOM_OUT),
  );
}
